import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePaystackPayment } from 'react-paystack';
import { CreditCard, ArrowLeft, ShieldCheck, Loader2, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/useToast';
import Card from '../components/ui/Card';
import { fetchPKForStore, client } from "../apiHelper"; // Imported as requested

const MakePayment = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showToast } = useToast();

    // Deconstruct parameters forwarded securely from the Checkout manifest state
    const {
        ward,
        amountOwed = 0,
        availableDeposit = 0,
        cartItems = [],
        subtotal = 0,
        finalCalculatedPayment = ''
    } = location.state || {};

    const [amount, setAmount] = useState(finalCalculatedPayment ? finalCalculatedPayment.toString() : '');
    const [publicKey, setPublicKey] = useState(null);
    const [loadingKey, setLoadingKey] = useState(true);
    const [processing, setProcessing] = useState(false);

    const [keyName, setKeyName] = useState("PAYSTACK_PUBLIC");
    const [gateway, setGateway] = useState('paystack'); // 'paystack' or 'seerbit'
    const [seerbitPublicKey, setSeerbitPublicKey] = useState(null);
    const [keyNameSeerbit] = useState('SEERBIT_PUBLIC');
    const [seerbitLoaded, setSeerbitLoaded] = useState(false);

    // Dynamic extraction of parent ID from active storage context
    const parentUserId = JSON.parse(localStorage.getItem("user"))?.users_id;

    // Safety fallback block if route is accessed without appropriate state parameters
    useEffect(() => {
        if (!ward || !ward.member_id) {
            showToast("Missing required checkout transaction properties.", "error");
            navigate('/dashboard');
        }
    }, [ward, navigate, showToast]);

    // Fetch the Store Specific Public Key dynamically based on branch settings
    useEffect(() => {
        const getStorePublicKey = async () => {
            const storeId = ward?.branches?.[0]?.store_id;
            if (!storeId) {
                showToast("Target branch store tracking profile not identified.", "error");
                setLoadingKey(false);
                return;
            }

            try {
                setLoadingKey(true);
                // Execute integration endpoint mapping with storeId integer param
                const pkResponse = await fetchPKForStore({ key: keyName, store_id: parseInt(storeId, 10) });

                const key = typeof pkResponse === 'string' ? pkResponse : `${pkResponse?.stringvalue}` || pkResponse?.data;
                setPublicKey(key);
            } catch (err) {
                // console.error("Error retrieving custom Paystack client key:", err);
                showToast(err.response?.data?.detail || "Could not configure payment gateway rules securely.", "error");
            } finally {
                setLoadingKey(false);
            }
        };

        if (ward) {
            getStorePublicKey();
        }
    }, [ward, showToast]);

    useEffect(() => {
        const getSeerbitPublicKey = async () => {
            const storeId = ward?.branches?.[0]?.store_id;
            if (!storeId) return;

            try {
                const pkResponse = await fetchPKForStore({ key: keyNameSeerbit, store_id: parseInt(storeId, 10) });
                const key = typeof pkResponse === 'string' ? pkResponse : pkResponse?.stringvalue || pkResponse?.data;
                setSeerbitPublicKey(key);
            } catch (err) {
                console.error("Seerbit key fetch error:", err);
            }
        };

        if (ward) getSeerbitPublicKey();
    }, [ward, keyNameSeerbit]);

    // Preload Seerbit SDK on mount
    useEffect(() => {
        if (window.SeerbitPay) {
            setSeerbitLoaded(true);
            return;
        }

        const script = document.createElement('script');
        script.src = "https://checkout.seerbitapi.com/api/v2/seerbit.js";
        script.async = true;
        script.onload = () => setSeerbitLoaded(true);
        script.onerror = () => showToast("Failed to load Seerbit payment gateway", "error");
        document.body.appendChild(script);
    }, []);

    // Calculate variations if the user changes the field value manually
    const specifiedAmount = parseFloat(amount) || 0;
    let paymentVarianceStatus = "exact";
    if (specifiedAmount < finalCalculatedPayment) paymentVarianceStatus = "underpayment";
    if (specifiedAmount > finalCalculatedPayment) paymentVarianceStatus = "overpayment";

    // Configuration rules payload for Paystack API instantiation
    const config = {
        publicKey: publicKey || '',
        email: ward?.email1 || 'parent_customer@example.com',
        amount: Math.round(specifiedAmount * 100), // Converted to base subunits safely
        currency: 'NGN',
        metadata: {
            ward_id: ward?.member_id,
            ward_name: ward ? `${ward.first_name} ${ward.middle_name} ${ward.last_name}`.trim() : 'Unknown Ward',
            parent_user_id: parentUserId,
            payment_variance: paymentVarianceStatus,
            checkout_manifest: {
                cart_items: cartItems,
                original_subtotal: subtotal,
                original_arrears: amountOwed,
                applied_deposit_deduction: availableDeposit,
                expected_settlement: finalCalculatedPayment
            }
        },
        channels: ['card', 'bank_transfer', 'ussd']
    };

    const initializePayment = usePaystackPayment(config);

    const handleSuccess = async (response) => {
        setProcessing(true);

        const selectedChannel = response.channel || "bank";
        try {
            // Build unified state package for submission to your backend database endpoints
            const databaseOrderPayload = {
                transaction_reference: response.reference,
                payment_channel: selectedChannel,
                ward_id: ward.member_id,
                store_id: ward?.branches?.[0]?.store_id,
                parent_user_id: parentUserId,
                expected_settlement: finalCalculatedPayment,
                amount_paid: specifiedAmount,
                payment_status: paymentVarianceStatus,
                gateway_key: keyName,
                items: cartItems,
                ledger_balances: {
                    arrears_settled: amountOwed,
                    deposits_utilized: availableDeposit
                }
            };

            // console.log(databaseOrderPayload)

            // record the core portal interaction data
            const recordresponse = await client.postData('/school/record-portal-payment', databaseOrderPayload);
            showToast(recordresponse.msg || 'Portal payment was successfully recorded', 'info');

            console.log("Submitting order context alongside transaction ref to backend:", databaseOrderPayload);
            const server_response = await client.postData("/school/finalize-school-fees-payment", databaseOrderPayload);

            showToast(server_response.msg, 'success');
            navigate('/dashboard')

        } catch (err) {
            console.error(err)
            showToast(err.response?.data?.detail || 'Payment successful, but database ledger logging failed. Contact Support.', 'error');
        } finally {
            setProcessing(false);
        }
    };

    const handleClose = () => {
        showToast('Payment processing sequence canceled by client.', 'info');
    };

    const handleSeerbitSuccess = async (response, closeModal) => {
        setProcessing(true);

        // Safely extract properties from Seerbit's callback response object
        const seerbitData = response?.payments || response?.data?.payments || response;
        const transactionRef = seerbitData?.paymentReference || response?.paymentReference || response?.tranref;
        const paymentChannel = seerbitData?.paymentType || response?.channel || "card";

        try {
            const databaseOrderPayload = {
                transaction_reference: transactionRef,
                payment_channel: paymentChannel.toLowerCase(),
                ward_id: ward.member_id,
                store_id: ward?.branches?.[0]?.store_id,
                parent_user_id: parentUserId,
                expected_settlement: finalCalculatedPayment,
                amount_paid: specifiedAmount,
                payment_status: paymentVarianceStatus, // Uses dynamic evaluation (exact/underpayment/overpayment)
                gateway_key: keyNameSeerbit,
                items: cartItems,
                ledger_balances: {
                    arrears_settled: amountOwed,
                    deposits_utilized: availableDeposit,
                }
            };

            console.log(databaseOrderPayload);

            // Record portal transaction record
            await client.postData('/school/record-portal-payment', databaseOrderPayload);

            // Finalize transaction ledger balance
            const server_response = await client.postData('/school/finalize-school-fees-payment', databaseOrderPayload);

            showToast(server_response.msg || 'Payment recorded successfully', 'success');

            // Close Seerbit Modal explicitly if provided by the SDK callback
            if (typeof closeModal === 'function') {
                closeModal();
            }

            // // Navigate away after successful recording
            navigate('/dashboard');

        } catch (err) {
            console.error("Seerbit settlement recording error:", err);
            showToast(err.response?.data?.detail || 'Payment successful on gateway, but database ledger update failed.', 'error');
        } finally {
            setProcessing(false);
        }
    };

    const handleSeerbitClose = () => {
        showToast('Payment window closed', 'info');
    }

    const initiateSeerbitPayment = () => {
        if (!seerbitPublicKey) {
            showToast('Seerbit API key is missing.', 'error');
            return;
        }

        const paymentData = {
            public_key: seerbitPublicKey,
            amount: parseFloat(amount),
            email: ward?.email1 || 'customer@email.com',
            tranref: `SB-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`, // 'tranref' or 'paymentReference'
            currency: 'NGN',
            full_name: `${ward?.first_name || ''} ${ward?.middle_name || ''} ${ward?.last_name || ''}`.trim() || "Customer",
        };

        // Check for SeerbitPay on the global window object
        if (window.SeerbitPay) {
            // window.SeerbitPay(paymentData, handleSeerbitSuccess, handleSeerbitClose);
            window.SeerbitPay(
                paymentData,
                (response, closeModal) => handleSeerbitSuccess(response, closeModal),
                handleSeerbitClose
            );
        } else {
            showToast('Seerbit gateway is still loading. Please try again in a moment.', 'info');
        }
    };

    const initiateTransaction = (e) => {
        e.preventDefault();
        if (!amount || parseFloat(amount) <= 0) {
            showToast('Please declare a valid settlement amount.', 'error');
            return;
        }
        if (gateway === 'paystack') {
            if (!publicKey) {
                showToast('Payment terminal configurations are currently unavailable.', 'error');
                return;
            }
            initializePayment({
                onSuccess: handleSuccess,
                onClose: handleClose
            });
        } else if (gateway === 'seerbit') {
            if (!seerbitPublicKey) {
                showToast('Payment terminal configuration unavailable', 'error');
                return;
            }
            // Seerbit will be initialized here (we'll add the SDK call next)
            initiateSeerbitPayment();
        }
    };

    if (loadingKey) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
                <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                <p className="text-sm font-medium text-zinc-500">Securing payment channel handshakes...</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-zinc-50/50 p-6 sm:p-8 space-y-6 max-w-2xl mx-auto">
            <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-sm font-medium text-zinc-500 hover:text-zinc-800 transition-colors cursor-pointer group"
            >
                <ArrowLeft size={16} className="transform group-hover:-translate-x-0.5 transition-transform" />
                Back to Order Details
            </button>

            <header className="space-y-1">
                <h1 className="text-2xl font-extrabold text-zinc-900 tracking-tight">Process Settlement Gateway</h1>
                <p className="text-sm text-zinc-500">
                    Executing transaction setup for <span className="font-semibold text-zinc-800">{ward?.first_name} {ward?.last_name}</span>
                </p>
            </header>

            <Card className="p-6 bg-white border-zinc-200/60 shadow-sm">
                <form onSubmit={initiateTransaction} className="space-y-6">
                    <div>
                        <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">
                            Settlement Balance Amount (NGN)
                        </label>
                        <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 font-medium text-sm">₦</span>
                            <input
                                type="number"
                                min="1"
                                step="0.01"
                                required
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="0.00"
                                className="w-full pl-8 pr-4 py-3 border border-zinc-200 rounded-xl text-base font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition-all"
                            />
                        </div>

                        {/* Dynamic variance note */}
                        {finalCalculatedPayment > 0 && specifiedAmount !== finalCalculatedPayment && (
                            <p className={`text-[11px] mt-1.5 font-medium italic ${paymentVarianceStatus === "underpayment" ? "text-amber-600" : "text-blue-600"}`}>
                                Note: You are processing an <strong>{paymentVarianceStatus}</strong> (Expected evaluation: ₦{parseFloat(finalCalculatedPayment).toLocaleString(undefined, { minimumFractionDigits: 2 })})
                            </p>
                        )}

                        {/* NEW: Prominent Bank Transfer Warning Reminder */}
                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
                            <AlertCircle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                            <div className="space-y-0.5">
                                <h4 className="text-[11px] font-bold text-amber-800 uppercase tracking-wide">Bank Transfer Reminder</h4>
                                <p className="text-[11px] text-amber-700 font-medium leading-relaxed">
                                    If choosing <strong>Bank Transfer</strong>, please send the exact amount down to the last Kobo. Do not round up, round down, or allow your bank to deduct transfer fees from this sum.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-2 mt-6 border border-zinc-200 rounded-2xl p-1 bg-zinc-50">
                        <button
                            type="button"
                            onClick={() => setGateway('paystack')}
                            className={`flex-1 py-3 rounded-xl font-semibold transition-all ${gateway === 'paystack' ? 'bg-white shadow-sm text-indigo-700' : 'text-zinc-700'}`}
                        >
                            Pay with Paystack
                        </button>
                        <button
                            type="button"
                            onClick={() => setGateway('seerbit')}
                            className={`flex-1 py-3 rounded-xl font-semibold transition-all ${gateway === 'seerbit' ? 'bg-white shadow-sm text-indigo-700' : 'text-zinc-600'}`}
                            // disabled
                        >
                            Pay with Seerbit
                        </button>
                    </div>

                    <div className="p-3 bg-zinc-50 border border-zinc-100 rounded-xl flex items-start gap-2.5">
                        <ShieldCheck size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                        <p className="text-[11px] text-zinc-500 leading-normal">
                            Payments are secured and processed via Paystack dynamically targeted to <strong>{ward?.branches?.[0]?.identifier || "Assigned Institution Branch"}</strong>.
                        </p>
                    </div>

                    <button
                        type="submit"
                        disabled={
                            processing ||
                            (gateway === 'paystack' && !publicKey) ||
                            (gateway === 'seerbit' && !seerbitPublicKey)
                        }
                        className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-zinc-200 text-white font-semibold text-sm rounded-xl transition-colors shadow-sm flex items-center justify-center gap-2 cursor-pointer"
                    >
                        {processing ? (
                            <>
                                <Loader2 size={16} className="animate-spin" /> Finalizing Database Ledger...
                            </>
                        ) : (
                            <>
                                <CreditCard size={16} /> Secure Payment Window
                            </>
                        )}
                    </button>
                </form>
            </Card>
        </div>
    );
};

export default MakePayment;